import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import {
  type Contract,
  type ContractFile,
  type CreateContractPayload,
  type GetContractsParams,
  type PaginatedContractsResponse,
  type UpdateContractPayload,
} from '../utils/types';
import { contractKeys } from '../utils/contract.keys';
import { contractApi } from '../api/contract.api';

type ContractsQueryOptions = Omit<
  UseQueryOptions<PaginatedContractsResponse, Error>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: Error) => void;
};

type ContractQueryOptions = Omit<
  UseQueryOptions<Contract, Error>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: Error) => void;
};

type CreateContractVariables = CreateContractPayload;
type UpdateContractVariables = { id: string; data: UpdateContractPayload };
type DeleteContractVariables = string;
type UploadFileVariables = { id: string; file: File };


export function useContracts(filters?: GetContractsParams, options?: ContractsQueryOptions) {
  const { onError: handleError, ...queryOptions } = options ?? {};

  const query = useQuery({
    queryKey: contractKeys.list(filters),
    queryFn: ({ signal }) => contractApi.list(filters, signal),
    staleTime: 60_000,
    ...(queryOptions as Omit<UseQueryOptions<PaginatedContractsResponse, Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;

    toast({
      title: 'Erro ao buscar contratos',
      description: query.error.message,
      variant: 'destructive',
    });

    handleError?.(query.error);
  }, [handleError, query.error]);


  return {
    contracts: query.data?.data ?? [],
    totalPages: query.data?.totalPage,
    totalItems: query.data?.totalItems ?? 0,
    currentPage: query.data?.page ?? filters?.page ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useContract(id?: string, options?: ContractQueryOptions) {
  const queryKey = id ? contractKeys.detail(id) : contractKeys.detail('');

  const { onError: handleError, ...queryOptions } = options ?? {};

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      if (!id) {
        throw new Error('ID do contrato não informado');
      }
      return contractApi.get(id, signal);
    },
    enabled: Boolean(id),
    ...(queryOptions as Omit<UseQueryOptions<Contract, Error>, 'queryKey' | 'queryFn'>),
  });

  useEffect(() => {
    if (!query.error) return;

    toast({
      title: 'Erro ao carregar contrato',
      description: query.error.message,
      variant: 'destructive',
    });

    handleError?.(query.error);
  }, [handleError, query.error]);

  const contract = query.data
    ? {
      ...query.data,
      files: query.data.files ?? [],
    }
    : null;

  return {
    contract,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreateContract(
  options?: UseMutationOptions<Contract, Error, CreateContractVariables>
) {
  const queryClient = useQueryClient();

  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = (options ??
    {}) as UseMutationOptions<Contract, Error, CreateContractVariables> & {
      onSuccess?: UseMutationOptions<Contract, Error, CreateContractVariables>['onSuccess'];
      onError?: UseMutationOptions<Contract, Error, CreateContractVariables>['onError'];
    };

  const mutation = useMutation({
    mutationKey: [...contractKeys.all, 'create'],
    mutationFn: async (payload: CreateContractVariables) => {
      const contract = await contractApi.create(payload);

      if (payload.file) {
        await contractApi.uploadFile(contract.id, payload.file);
      }

      return contract;
    },
    ...mutationOptions,
    onSuccess: (contract, variables, onMutateResult, context) => {
      toast({
        title: 'Contrato criado com sucesso',
        variant: 'success',

      });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
      providedOnSuccess?.(contract, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao criar contrato',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    createContract: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useUpdateContract(
  options?: UseMutationOptions<Contract, Error, UpdateContractVariables>
) {
  const queryClient = useQueryClient();

  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = (options ??
    {}) as UseMutationOptions<Contract, Error, UpdateContractVariables> & {
      onSuccess?: UseMutationOptions<Contract, Error, UpdateContractVariables>['onSuccess'];
      onError?: UseMutationOptions<Contract, Error, UpdateContractVariables>['onError'];
    };

  const mutation = useMutation({
    mutationKey: [...contractKeys.all, 'update'],
    mutationFn: async ({ id, data }: UpdateContractVariables) => {
      const contract = await contractApi.update(id, data);

      if (data.file) {
        await contractApi.uploadFile(id, data.file);
      }

      return contract;
    },
    ...mutationOptions,
    onSuccess: (contract, variables, onMutateResult, context) => {
      toast({
        title: 'Contrato atualizado com sucesso',
        variant: 'success',

      });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
      providedOnSuccess?.(contract, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao atualizar contrato',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  const updateContract = (id: string, data: UpdateContractPayload) =>
    mutation.mutateAsync({ id, data });

  return {
    updateContract,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useDeleteContract(
  options?: UseMutationOptions<{ id: string; deletedAt: string }, Error, DeleteContractVariables>
) {
  const queryClient = useQueryClient();

  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = (options ??
    {}) as UseMutationOptions<{ id: string; deletedAt: string }, Error, DeleteContractVariables> & {
      onSuccess?: UseMutationOptions<
        { id: string; deletedAt: string },
        Error,
        DeleteContractVariables
      >['onSuccess'];
      onError?: UseMutationOptions<
        { id: string; deletedAt: string },
        Error,
        DeleteContractVariables
      >['onError'];
    };

  const mutation = useMutation({
    mutationKey: [...contractKeys.all, 'delete'],
    mutationFn: (contractId: DeleteContractVariables) => contractApi.remove(contractId),
    ...mutationOptions,
    onSuccess: (response, variables, onMutateResult, context) => {
      toast({
        title: 'Contrato excluído com sucesso',
        variant: 'success',

      });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
      providedOnSuccess?.(response, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao excluir contrato',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  const deleteContract = (contractId: string) => mutation.mutateAsync(contractId);

  return {
    deleteContract,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function useUploadContractFile(
  options?: UseMutationOptions<ContractFile, Error, UploadFileVariables>
) {
  const queryClient = useQueryClient();

  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = (options ??
    {}) as UseMutationOptions<ContractFile, Error, UploadFileVariables> & {
      onSuccess?: UseMutationOptions<ContractFile, Error, UploadFileVariables>['onSuccess'];
      onError?: UseMutationOptions<ContractFile, Error, UploadFileVariables>['onError'];
    };

  const mutation = useMutation({
    mutationKey: [...contractKeys.all, 'upload-file'],
    mutationFn: ({ id, file }: UploadFileVariables) => contractApi.uploadFile(id, file),
    ...mutationOptions,
    onSuccess: (file, variables, onMutateResult, context) => {
      toast({
        title: 'Arquivo enviado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(variables.id) });
      providedOnSuccess?.(file, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao enviar arquivo',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });

  const uploadContractFile = (contractId: string, file: File) =>
    mutation.mutateAsync({ id: contractId, file });

  return {
    uploadContractFile,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
